import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Leaf, Menu, X, User, LogOut, Calendar, Settings, Globe, Shield, ChevronDown, BookOpen, FileText, Newspaper, Info, Star, ShoppingBag } from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isPractitioner, isAdmin } = useAuth();
  const { t, language, setLanguage, languages } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    return isPractitioner ? '/practitioner/dashboard' : '/client/dashboard';
  };

  const isActive = (path) => location.pathname === path;

  const currentLang = languages.find(l => l.code === language);

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sand-200" data-testid="navbar">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-sage-500 rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-xl font-semibold text-foreground">Opora Vie</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/search" 
              className={`nav-link ${isActive('/search') ? 'nav-link-active' : ''}`}
              data-testid="nav-search"
            >
              {t('nav.findPractitioner')}
            </Link>
            <Link 
              to="/about" 
              className={`nav-link ${isActive('/about') ? 'nav-link-active' : ''}`}
              data-testid="nav-about"
            >
              {t('nav.about')}
            </Link>

            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="nav-link flex items-center gap-1 hover:bg-transparent"
                  data-testid="nav-resources-trigger"
                >
                  {t('nav.resources') || 'Ressources'}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/practical-info" className="flex items-center gap-2 cursor-pointer" data-testid="nav-practical-info">
                    <Info className="w-4 h-4" />
                    {t('nav.practicalInfo') || 'Infos pratiques'}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/resources" className="flex items-center gap-2 cursor-pointer" data-testid="nav-resources">
                    <BookOpen className="w-4 h-4" />
                    {t('nav.resourcesGuides') || 'Guides & Conseils'}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/blog" className="flex items-center gap-2 cursor-pointer" data-testid="nav-blog">
                    <Newspaper className="w-4 h-4" />
                    {t('nav.blog') || 'Blog / Actualités'}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Crédit link - standalone */}
            <Link 
              to="/une" 
              className={`nav-link ${isActive('/une') ? 'nav-link-active' : ''}`}
              data-testid="nav-credits"
            >
              Crédit
            </Link>

            {/* Boutique link */}
            <Link 
              to="/boutique" 
              className={`nav-link flex items-center gap-1 ${isActive('/boutique') ? 'nav-link-active' : ''}`}
              data-testid="nav-boutique"
            >
              <ShoppingBag className="w-4 h-4" />
              Boutique
            </Link>

            {/* Aria link */}
            <Link 
              to="/aria" 
              className={`nav-link ${isActive('/aria') ? 'nav-link-active' : ''}`}
              data-testid="nav-aria"
            >
              Aria
            </Link>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="flex items-center gap-2 hover:bg-sage-50"
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4" />
                  <span>{currentLang?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-sage-50' : ''}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 hover:bg-sage-50"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-sage-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-sage-600" />
                    </div>
                    <span className="font-medium">{user?.first_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2 cursor-pointer" data-testid="nav-dashboard">
                      <Calendar className="w-4 h-4" />
                      {t('nav.mySpace')}
                    </Link>
                  </DropdownMenuItem>
                  {isPractitioner && (
                    <DropdownMenuItem asChild>
                      <Link to="/practitioner/setup" className="flex items-center gap-2 cursor-pointer" data-testid="nav-settings">
                        <Settings className="w-4 h-4" />
                        {t('nav.myPractitionerProfile')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="nav-admin">
                        <Shield className="w-4 h-4" />
                        Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600 cursor-pointer"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/auth">
                  <Button variant="ghost" className="nav-link" data-testid="nav-login">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/auth?tab=register">
                  <Button className="btn-primary" data-testid="nav-register">
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2">
                  <span>{currentLang?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="cursor-pointer"
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button 
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-sand-200" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link 
                to="/search" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.findPractitioner')}
              </Link>
              <Link 
                to="/about" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/practical-info" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.practicalInfo') || 'Infos pratiques'}
              </Link>
              <Link 
                to="/resources" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.resourcesGuides') || 'Guides & Conseils'}
              </Link>
              <Link 
                to="/blog" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.blog') || 'Blog / Actualités'}
              </Link>
              <Link 
                to="/une" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Crédit
              </Link>
              <Link 
                to="/boutique" 
                className="nav-link py-2 flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ShoppingBag className="w-4 h-4" />
                Boutique
              </Link>
              <Link 
                to="/aria" 
                className="nav-link py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Aria
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link 
                    to={getDashboardLink()} 
                    className="nav-link py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.mySpace')}
                  </Link>
                  {isPractitioner && (
                    <Link 
                      to="/practitioner/setup" 
                      className="nav-link py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.myPractitionerProfile')}
                    </Link>
                  )}
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="nav-link py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Administration
                    </Link>
                  )}
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="text-left text-red-600 py-2"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/auth" 
                    className="nav-link py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.login')}
                  </Link>
                  <Link 
                    to="/auth?tab=register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="btn-primary w-full">{t('nav.register')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
